import {
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react'

import { useQueryClient } from '@tanstack/react-query'
import styled, { css } from 'styled-components'

import { triggerAureusImpression } from '@utils/aureus'
import useViewportTracking from '@hooks/useViewportTracking'
import { StoryAPIProps } from '@widgets/Toplist'
import Blink from '@components/Blink'
import SvgIcon from '@components/SvgIcon'

export interface StoryProps
  extends Pick<
    StoryAPIProps,
    'title' | 'catchword' | 'link' | 'articleId' | 'isPlus' | 'aureusOfferId'
  > {
  onClick?: () => void
  order: number
}

const StyledWrapper = styled.li`
  min-width: 0;
  list-style-type: none;
`

const StyledBlink = styled(Blink)`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 0;
  text-decoration: none;
`

const StyledOrder = styled.div`
  ${({
    theme: {
      colors: { textBrand },
      typography: {
        headings: {
          xxxlarge: { bundledCSS: xxxLargeHeadingCSS },
        },
      },
    },
  }) => css`
    ${xxxLargeHeadingCSS};
    color: ${textBrand};
    min-width: 28px;
  `}
`

const StyledStoryContent = styled.div`
  ${({
    theme: {
      spacing: { spacing12 },
    },
  }) => css`
    flex: 1;
    display: flex;
    flex-direction: column;
    margin-left: ${spacing12};
    overflow: hidden;
    justify-content: center;
  `}
`

const StyledTitle = styled.div`
  ${({
    theme: {
      typography: {
        headings: {
          xsmall: { bundledCSS: xsmallHeadingCSS },
        },
      },
      spacing: { spacing4 },
      colors: { textDefault },
      utils: { ellipsis },
    },
  }) => {
    return css`
      ${xsmallHeadingCSS};
      font-style: normal;
      text-align: left;
      margin-top: ${spacing4};
      color: ${textDefault};

      ${ellipsis('100%', 2)}
    `
  }}
`

const StyledCatchword = styled.div`
  ${({
    theme: {
      typography: {
        subheads: {
          small1: { bundledCSS: small1SubheadingCSS },
        },
      },
      colors: { textBrand },
      utils: { ellipsis },
    },
  }) => {
    return css`
      ${small1SubheadingCSS};
      text-align: left;
      color: ${textBrand};

      ${ellipsis('100%', 1)}
    `
  }}
`

const CatchwordAndPlusLogoContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const BlickPlusIcon = styled(SvgIcon)`
  ${({
    theme: {
      spacing: { spacing4 },
    },
  }) => {
    return css`
      margin-right: ${spacing4};
      height: 18px;
    `
  }}
`

const Story: FunctionComponent<PropsWithChildren<StoryProps>> = ({
  title,
  catchword = '',
  order,
  link,
  isPlus,
  onClick,
  aureusOfferId,
  children,
}) => {
  const queryClient = useQueryClient()
  const [impressionOccured, setImpressionOccured] = useState(false)

  const onStoryImpression = useCallback(() => {
    setImpressionOccured(true)
  }, [])

  useEffect(() => {
    if (aureusOfferId && impressionOccured) {
      triggerAureusImpression({
        aureusOfferId,
        queryClient,
      })
    }
  }, [impressionOccured, onStoryImpression, aureusOfferId, queryClient])

  const viewportRef = useViewportTracking({
    onImpression: onStoryImpression,
    track: true,
  })

  return (
    <StyledWrapper ref={viewportRef}>
      <StyledBlink {...link} aureusOfferId={aureusOfferId} onClick={onClick}>
        <StyledOrder>{order}</StyledOrder>
        <StyledStoryContent>
          <CatchwordAndPlusLogoContainer>
            {isPlus && <BlickPlusIcon iconName="blick-plus-logo" size={18} />}
            {!!catchword && <StyledCatchword>{catchword}</StyledCatchword>}
          </CatchwordAndPlusLogoContainer>
          <StyledTitle>{title}</StyledTitle>
          {children}
        </StyledStoryContent>
      </StyledBlink>
    </StyledWrapper>
  )
}

export default Story
